<template>
  <button
    class="flex relative items-center transition p-4"
    :class="[sizeClasses, colorClasses, disabledClasses, iconClasses]"
  >
    <l-icon v-if="icon" :size="size" :name="icon" />
    <span class="truncate"><slot></slot></span>
    <div v-if="type === 'vertical' && $slots.description" class="body-4 text-n-100 text-md">
      <slot name="description"></slot>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { LIconName } from '@last/core-ui/paprika'
import { LIcon } from '@last/core-ui/paprika'

interface Props {
  /**
   * Button type
   * @default primary
   * */
  type?: 'primary' | 'vertical'
  /**
   * Button size
   * @default medium
   * */
  size?: 'small' | 'medium' | 'large'
  /**
   * Whether the button is selected
   * @default false
   */
  isSelected?: boolean
  /**
   * Disable the button
   * @default false
   */
  disabled?: boolean
  /**
   * Icon alignment
   * @default left
   * */
  iconAlign?: 'left' | 'right'
  /**
   * Icon name
   * */
  icon?: LIconName
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  type: 'primary',
  loading: false,
  disabled: false,
  iconAlign: 'left',
  icon: undefined
})

const sizeClasses = computed<string[]>(() => {
  const common = ['rounded-lg', 'hover:scale-[1.03]']
  if (props.type === 'vertical')
    return ['h-[4.75rem]', 'body-3', 'flex-col', 'justify-center', 'w-full', ...common]
  if (props.size === 'small') return ['body-3', 'h-9', 'gap-2 ', ...common]
  if (props.size === 'large') return ['body-1', 'h-14', 'gap-2 ', ...common]
  return ['body-2', 'h-11', 'gap-2 ', ...common]
})

const colorClasses = computed<string[]>(() => {
  return [
    'text-n-0',
    'shadow-[0_0_16x_0_rgba(0,0,0,0.25),0_4px_8px_0_rgba(0,0,0,0.20)]',
    'hover:shadow-[0_2px_20px_0_rgba(0,0,0,0.25),0_6px_12px_0_rgba(0,0,0,0.20)]',
    'active:shadow-[0_0x_8px_0_rgba(0,0,0,0.25),0_2px_4px_0_rgba(0,0,0,0.20)]',
    props.isSelected ? 'bg-v-300' : 'bg-n-600 hover:bg-n-700'
  ]
})

const disabledClasses = computed<string[]>(() => {
  return props.disabled ? ['opacity-50', 'cursor-not-allowed', 'pointer-events-none'] : []
})

const iconClasses = computed<string[]>(() => {
  return props.iconAlign === 'right' ? ['row-reverse'] : []
})
</script>
